import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppInitializedAction } from '@app/app.actions';
import { getLoginTimestamp } from '@app/app.reducer';
import { FeatureManager } from '@core/data-access/services/feature-manager.service';
import { SavedTicketHolderActions } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import { isLoginSessionExpired } from '@core/utils/datetime-helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { WAIT_FOR_QUERY_PARAMS_TO_BE_SET_DEBOUNCE_TIME } from '@shared/tokens';
import { GetOperatorSettingWithEventEndpoint } from '@store/customization/customization.actions';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { ActionTypes as UserActionTypes } from '@store/user/user.actions';
import { has } from 'lodash';
import { debounceTime, filter, first, map, switchMap, tap } from 'rxjs';

export const addSavedTicketHolderFeature$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    activatedRoute = inject(ActivatedRoute),
    waitForQueryPramsToBeSetDebounceTime = inject(WAIT_FOR_QUERY_PARAMS_TO_BE_SET_DEBOUNCE_TIME),
    featureManager = inject(FeatureManager)
  ) =>
    actions$.pipe(
      ofType(AppInitializedAction, UserActionTypes.SET_PROFILE),
      switchMap(() => activatedRoute.queryParams),
      debounceTime(waitForQueryPramsToBeSetDebounceTime),
      concatLatestFrom(() => store.select(getLoginTimestamp)),
      filter(([queryParams, loginTimestamp]) => !has(queryParams, 'crmHash') && !isLoginSessionExpired(loginTimestamp)),
      switchMap(() =>
        store.pipe(
          select(getIsTranslationLoaded),
          first((isTranslationLoaded) => isTranslationLoaded)
        )
      ),
      tap(() => featureManager.addSavedTicketHolderFeature())
    ),
  { functional: true, dispatch: false }
);

export const getOperatorSettingsWithEventEndpoint = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(AppInitializedAction),
      map(() => new GetOperatorSettingWithEventEndpoint())
    ),
  { functional: true }
);

export const resetSavedTicketHolderReducer$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserActionTypes.RESET_REDUCER),
      map(() => SavedTicketHolderActions.resetReducer())
    ),
  { functional: true }
);

export const removeSavedTicketHolderFeature$ = createEffect(
  (actions$ = inject(Actions), featureManager = inject(FeatureManager)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.resetReducer),
      tap(() => featureManager.removeSavedTicketHolderFeature())
    ),
  { functional: true, dispatch: false }
);
